import Button from '@atlaskit/button';
import { Field } from '@atlaskit/form';
import Textfield from '@atlaskit/textfield';
import SectionMessage from '@atlaskit/section-message';

import React, { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { Dimensions } from 'components/visuals';
import { State } from 'model/State';
import t from 'modules/i18n/intl';
import { messages } from 'modules/mac/messages';
import { fetchGetCustomerByEmail } from 'modules/mac/duck/actions/contact';
import {
  getCurrentMACContactEmail,
  selectGetCustomerByEmail,
  selectNewContact,
} from 'modules/mac/duck/selectors';
import {
  addContactAsMain,
  addNewContactToAccount,
  fetchDetails,
  makeContactPrimary,
} from 'modules/mac/duck/actions/accounts';
import validateEmailAddress from 'util/email';
import { MessageDescriptor } from 'util/lib/types';
import { deleteFromHamsClient } from 'util/mac/api/account/delete';
import { AddContactModal } from './AddContactModal';
import { ContactsActions } from './ContactsActions';
import { MappedContact, ContactTypes } from './licensesExpandedRow';
import { MakePrimaryModal } from './MakePrimary/MakePrimaryModal';
import {
  AddContactWrapper,
  FieldWrapper,
  StyledContactsHeading,
  StyledDivWithBorder,
} from './StyledComponents';
import { TechnicalTooltip } from './Tooltips/Technical';

type PropTypes = {
  technicalContacts: Array<MappedContact>;
  billingContacts: Array<MappedContact>;
  showContacts?: boolean;
  accountId: string;
  isOpen: boolean;
  openModal: () => any;
  closeModal: () => any;
};

const validateEmail = (email: string) => {
  return (
    validateEmailAddress(email) &&
    !email.endsWith('@atlassian.com') &&
    !email.endsWith('@gmail.com') &&
    !email.endsWith('@hotmail.com') &&
    !email.endsWith('@outlook.com')
  );
};

export type ContactTypeNames = 'technical' | 'billing';
type PromotionError = 'irreconcilable' | 'failed';

const ContactsSection: React.FC<PropTypes> = ({
  technicalContacts,
  billingContacts,
  showContacts,
  accountId,
  isOpen,
  openModal,
  closeModal,
}) => {
  const dispatch = useDispatch();
  const userEmail = useSelector(getCurrentMACContactEmail) ?? '';
  const customersByEmail = useSelector(selectGetCustomerByEmail);
  const state = useSelector<State, State>((s) => s);
  const [promotionModalOpen, setPromotionModalOpen] = useState<ContactTypeNames>();

  const [newBillingContact, setNewBillingContact] = useState('');
  const [newTechnicalContact, setNewTechnicalContact] = useState('');
  const [isTechnicalContactInvalid, setIsTechnicalContactInvalid] = useState(false);
  const [isBillingContactInvalid, setIsBillingContactInvalid] = useState(false);
  const [newContactType, setNewContactType] = useState('');
  const [newContact, setNewContact] = useState('');
  const [showTechnicalContactsLimitError, setShowTechnicalContactsLimitError] = useState(false);
  const [showBillingContactsLimitError, setShowBillingContactsLimitError] = useState(false);
  const [deletionErrorMessage, setDeletionErrorMessage] = useState<MessageDescriptor>();
  const [deletionError, setDeletionError] = useState();
  const newContactResponse = useSelector(selectNewContact);
  const [currentContactEmail, setCurrentContactEmail] = useState('');
  const [currentContactId, setCurrentContactId] = useState('');
  const [currentContactSmartDomain, setCurrentContactSmartDomain] = useState('');
  const [promotionError, setPromotionError] = useState<PromotionError>();
  const [promotionSucceed, setPromotionSucceed] = useState(false);

  const openPromotionModal = (kind: ContactTypeNames) => setPromotionModalOpen(kind);
  const closePromotionModal = () => setPromotionModalOpen(undefined);

  const deleteContact = (id: string, canBeDeleted: boolean) => () => {
    if (!canBeDeleted) setDeletionErrorMessage(t('mac.licenses.unable.to.delete'));
    else {
      deleteFromHamsClient(`/contactRelationship/${id}`, state)
        .then(() => {
          dispatch(fetchDetails(accountId));
        })
        .catch((error) => {
          setDeletionError(error);
          setDeletionErrorMessage(messages.errorOnDelete);
        });
    }
  };

  useEffect(() => {
    if (newContactResponse) {
      dispatch(fetchDetails(accountId));
    }
  }, [newContactResponse]);

  useEffect(() => {
    if (
      customersByEmail[newContact] &&
      !showTechnicalContactsLimitError &&
      !showBillingContactsLimitError
    ) {
      const result = customersByEmail[newContact];
      if (
        result?.result &&
        !technicalContacts.some(({ email }) => result?.result?.contactDetails?.email === email)
      ) {
        dispatch(
          addNewContactToAccount(accountId, result?.result.contactDetails?.id, newContactType),
        );
      } else if (result?.error) {
        openModal();
      }
    }
  }, [
    customersByEmail[newContact],
    showTechnicalContactsLimitError,
    showBillingContactsLimitError,
  ]);

  const handleAddTechnicalContactClick = () => {
    setNewContact(newTechnicalContact);
    if (!newTechnicalContact || !validateEmail(newTechnicalContact)) {
      setIsTechnicalContactInvalid(true);
    } else if (technicalContacts.length >= 20) {
      setShowTechnicalContactsLimitError(true);
      setIsTechnicalContactInvalid(true);
    } else {
      dispatch(fetchGetCustomerByEmail(newTechnicalContact));
    }
    setNewTechnicalContact('');
  };

  const handleAddBillingContactClick = () => {
    setNewContact(newBillingContact);
    if (!newBillingContact || !validateEmail(newBillingContact)) {
      setIsBillingContactInvalid(true);
    } else if (billingContacts.length >= 20) {
      setShowBillingContactsLimitError(true);
      setIsBillingContactInvalid(true);
    } else {
      dispatch(fetchGetCustomerByEmail(newBillingContact));
    }
    setNewBillingContact('');
  };

  const handlePromotion = (kind: 'technical' | 'billing') => {
    if (!currentContactEmail || !currentContactId) return;

    const promoteeIsContact = technicalContacts.find((c) => c.email === currentContactEmail);
    if (!promoteeIsContact) return;

    const promoterIsContact = technicalContacts.find((c) => c.email === userEmail);
    if (!promoterIsContact) return;

    const primaryTechnicalAccount = technicalContacts.find((c) => c.main);

    const primaryEmailOrDomain =
      currentContactEmail !== primaryTechnicalAccount?.email &&
      currentContactSmartDomain === primaryTechnicalAccount?.smartDomain;

    if (!primaryEmailOrDomain) return;

    try {
      dispatch(addContactAsMain(accountId, currentContactId, kind));
    } catch {
      setPromotionError('irreconcilable');
      return;
    }

    try {
      dispatch(makeContactPrimary(accountId, currentContactId, kind));
      setPromotionSucceed(true);
    } catch {
      setPromotionError('failed');
    }
  };

  return (
    <>
      <h3>
        <FormattedMessage {...messages.contactDetails} />
      </h3>
      <SectionMessage appearance="information">
        <FormattedMessage {...messages.technicalAddressOnInvoice} />
      </SectionMessage>
      {deletionErrorMessage && (
        <SectionMessage appearance="error">
          <FormattedMessage
            {...deletionErrorMessage}
            values={{ error: ((deletionError as unknown) as Error)?.message }}
          />
        </SectionMessage>
      )}
      <StyledDivWithBorder>
        <StyledContactsHeading>
          <FormattedMessage {...messages.technicalContacts} />
          &nbsp;
          <TechnicalTooltip />
        </StyledContactsHeading>
      </StyledDivWithBorder>
      <>
        {showContacts &&
          technicalContacts?.map(
            ({
              id,
              name,
              main,
              email,
              canBePromoted,
              inactiveMakePrimary,
              smartDomain,
              noMakePrimary,
              canBeDeleted,
              contactId,
            }) => (
              <ContactsActions
                key={id}
                deleteContact={deleteContact}
                name={name}
                email={email}
                main={main}
                contactId={contactId}
                noMakePrimary={noMakePrimary}
                canBePromoted={canBePromoted}
                inactiveMakePrimary={inactiveMakePrimary}
                canBeDeleted={canBeDeleted}
                openPromotionModal={openPromotionModal}
                setCurrentContactEmail={setCurrentContactEmail}
                setCurrentContactId={setCurrentContactId}
                setCurrentContactSmartDomain={setCurrentContactSmartDomain}
                smartDomain={smartDomain}
                id={id}
                kind="technical"
              />
            ),
          )}

        <AddContactWrapper>
          <FieldWrapper width={`${Dimensions.units(6.25)}%`}>
            <Field name="technical-contact" isRequired defaultValue="">
              {({ fieldProps }: any) => (
                <Textfield
                  {...fieldProps}
                  type="text"
                  data-testid="add-technical-contact"
                  placeholder="contact@example.com"
                  value={newTechnicalContact}
                  onChange={(e) => {
                    setShowTechnicalContactsLimitError(false);
                    setIsTechnicalContactInvalid(false);
                    setNewTechnicalContact(e.target.value);
                  }}
                  isInvalid={isTechnicalContactInvalid}
                  data-cy="technical-contact-input"
                />
              )}
            </Field>
          </FieldWrapper>
          <Button
            style={{ width: '15%' }}
            onClick={() => {
              setNewContactType(ContactTypes.TECHNICAL);
              handleAddTechnicalContactClick();
            }}
            appearance="primary"
            isDisabled={isTechnicalContactInvalid}
            data-cy="add-technical-contact-btn"
          >
            <FormattedMessage {...messages.addBtn} />
          </Button>
        </AddContactWrapper>
        {showTechnicalContactsLimitError && (
          <SectionMessage appearance="error">
            <FormattedMessage {...messages.contactsLimitReached} />
          </SectionMessage>
        )}
      </>
      <StyledDivWithBorder>
        <StyledContactsHeading>
          <FormattedMessage {...messages.billingContacts} />
          &nbsp;
          <TechnicalTooltip />
        </StyledContactsHeading>
      </StyledDivWithBorder>
      <div data-cy="billing-contacts-actions-row">
        {showContacts &&
          billingContacts?.map(
            ({
              id,
              name,
              main,
              email,
              noMakePrimary,
              inactiveMakePrimary,
              canBePromoted,
              smartDomain,
              canBeDeleted,
              contactId,
            }) => (
              <ContactsActions
                key={id}
                deleteContact={deleteContact}
                name={name}
                contactId={contactId}
                email={email}
                main={main}
                noMakePrimary={noMakePrimary}
                canBePromoted={canBePromoted}
                inactiveMakePrimary={inactiveMakePrimary}
                canBeDeleted={canBeDeleted}
                openPromotionModal={openPromotionModal}
                setCurrentContactEmail={setCurrentContactEmail}
                setCurrentContactId={setCurrentContactId}
                setCurrentContactSmartDomain={setCurrentContactSmartDomain}
                smartDomain={smartDomain}
                id={id}
                kind="billing"
              />
            ),
          )}
        <AddContactWrapper>
          <FieldWrapper width={`${Dimensions.units(6.25)}%`}>
            <Field name="billing-contact" isRequired defaultValue="">
              {({ fieldProps }: any) => (
                <Textfield
                  {...fieldProps}
                  type="text"
                  data-testid="add-billing-contact"
                  placeholder="contact@example.com"
                  value={newBillingContact}
                  onChange={(e) => {
                    setShowBillingContactsLimitError(false);
                    setIsBillingContactInvalid(false);
                    setNewBillingContact(e.target.value);
                  }}
                  isInvalid={isBillingContactInvalid}
                />
              )}
            </Field>
          </FieldWrapper>
          <Button
            onClick={() => {
              setNewContactType(ContactTypes.BILLING);
              handleAddBillingContactClick();
            }}
            style={{ width: '18%' }}
            appearance="primary"
            isDisabled={isBillingContactInvalid}
            data-cy="add-billing-contact-btn"
          >
            <FormattedMessage {...messages.addBtn} />
          </Button>
        </AddContactWrapper>
        {showBillingContactsLimitError && (
          <SectionMessage appearance="error">
            <FormattedMessage {...messages.contactsLimitReached} />
          </SectionMessage>
        )}
      </div>

      <AddContactModal
        isOpen={isOpen}
        closeModal={closeModal}
        newContact={newContact}
        accountId={accountId}
        newContactType={newContactType}
      />

      <MakePrimaryModal
        succeed={promotionSucceed}
        closeModal={closePromotionModal}
        contactId={currentContactId}
        accountId={accountId}
        email={currentContactEmail}
        contactRelationshipType={promotionModalOpen}
        handlePromotion={handlePromotion}
        promotionError={promotionError}
      />
    </>
  );
};
export default ContactsSection;
